import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ArrowLeft, FileText, Download, Calendar, LayoutTemplate, Save, Check, ChevronDown, ChevronUp, Settings2, AlignLeft, Eye, X, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { clients } from '../lib/data';
const TEMPLATES = [{
  id: 'executive',
  name: 'Executive Summary',
  description: 'Sintesi rapida per il top management. Solo i dati essenziali.',
  sectionsCount: 3,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: false,
    tier: false,
    sources: false,
    spokespersons: false,
    allArticles: false
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-883">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-884" />
        <div className="h-6 w-full bg-accent/20 rounded-sm" data-id="element-885" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-886">
          <div className="h-8 bg-muted rounded-sm" data-id="element-887" />
          <div className="h-8 bg-muted rounded-sm" data-id="element-888" />
        </div>
        <div className="h-10 w-full bg-muted rounded-sm mt-auto" data-id="element-889" />
      </div>
}, {
  id: 'standard',
  name: 'Report Standard',
  description: 'Il formato più utilizzato. Include analisi qualitativa e quantitativa.',
  sectionsCount: 5,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: true,
    tier: true,
    sources: false,
    spokespersons: false,
    allArticles: false
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-890">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-891" />
        <div className="h-6 w-full bg-accent/20 rounded-sm" data-id="element-892" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-893">
          <div className="h-8 bg-muted rounded-sm" data-id="element-894" />
          <div className="h-8 bg-muted rounded-sm" data-id="element-895" />
        </div>
        <div className="grid grid-cols-2 gap-1.5 mt-auto" data-id="element-896">
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-897" />
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-898" />
        </div>
      </div>
}, {
  id: 'full',
  name: 'Analisi Completa',
  description: 'Tutti i dati disponibili, inclusa la lista completa degli articoli.',
  sectionsCount: 8,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: true,
    tier: true,
    sources: true,
    spokespersons: true,
    allArticles: true
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-899">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-900" />
        <div className="h-4 w-full bg-accent/20 rounded-sm" data-id="element-901" />
        <div className="grid grid-cols-3 gap-1" data-id="element-902">
          <div className="h-6 bg-muted rounded-sm" data-id="element-903" />
          <div className="h-6 bg-muted rounded-sm" data-id="element-904" />
          <div className="h-6 bg-muted rounded-sm" data-id="element-905" />
        </div>
        <div className="grid grid-cols-2 gap-1.5" data-id="element-906">
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-907" />
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-908" />
        </div>
        <div className="h-8 w-full bg-muted/50 rounded-sm mt-auto" data-id="element-909" />
      </div>
}];
const AVAILABLE_SECTIONS = [{
  id: 'summary',
  label: 'Riepilogo AI (Executive Summary)'
}, {
  id: 'kpi',
  label: 'KPI Principali'
}, {
  id: 'topArticles',
  label: 'Top Articoli'
}, {
  id: 'sentiment',
  label: 'Grafico Sentiment'
}, {
  id: 'tier',
  label: 'Copertura per Tier'
}, {
  id: 'sources',
  label: 'Top Testate'
}, {
  id: 'spokespersons',
  label: 'Portavoce citati'
}, {
  id: 'allArticles',
  label: 'Lista completa articoli'
}];
export function ReportBuilder() {
  const navigate = useNavigate();
  const [reportName, setReportName] = useState('Report Mensile Aprile 2026');
  const [template, setTemplate] = useState('standard');
  const [sections, setSections] = useState(TEMPLATES[1].sections);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const intrumClient = clients.find(c => c.id === 'intrum') || clients[0];
  useEffect(() => {
    if (previewOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [previewOpen]);
  const handleTemplateChange = (templateId: string) => {
    setTemplate(templateId);
    const selectedTemplate = TEMPLATES.find(t => t.id === templateId);
    if (selectedTemplate) {
      setSections(selectedTemplate.sections);
    }
  };
  const toggleSection = (key: keyof typeof sections) => {
    setSections(prev => {
      const newSections = {
        ...prev,
        [key]: !prev[key]
      };
      // Check if it matches any template exactly
      const matchedTemplate = TEMPLATES.find(t => Object.entries(t.sections).every(([k, v]) => newSections[k as keyof typeof sections] === v));
      if (matchedTemplate) {
        setTemplate(matchedTemplate.id);
      } else {
        setTemplate('custom');
      }
      return newSections;
    });
  };
  const selectedCount = Object.values(sections).filter(Boolean).length;
  return <div className="h-full flex flex-col bg-background relative" data-id="element-910">
      {/* Header */}
      <div className="p-4 md:px-8 md:py-5 border-b border-border bg-card flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 sticky top-0 z-20" data-id="element-911">
        <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-912">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} className="rounded-full flex-shrink-0" data-id="element-913">
            <ArrowLeft className="h-5 w-5" data-id="element-914" />
          </Button>
          <div className="flex-1 min-w-0 flex items-center gap-3" data-id="element-915">
            <input type="text" value={reportName} onChange={e => setReportName(e.target.value)} className="text-xl md:text-2xl font-semibold bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 w-full max-w-[400px] truncate hover:bg-muted/30 px-2 -ml-2 rounded transition-colors cursor-text" data-id="element-916" />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0 ml-12 sm:ml-0" data-id="element-917">
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-md border border-border" data-id="element-918">
            <Calendar className="h-4 w-4" data-id="element-919" />
            <Select defaultValue="month" data-id="element-920">
              <SelectTrigger className="h-auto p-0 border-none bg-transparent shadow-none focus:ring-0 text-sm font-medium text-foreground w-[130px]" data-id="element-921">
                <SelectValue data-id="element-922" />
              </SelectTrigger>
              <SelectContent data-id="element-923">
                <SelectItem value="7days" data-id="element-924">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30days" data-id="element-925">Ultimi 30 giorni</SelectItem>
                <SelectItem value="month" data-id="element-926">Mese corrente</SelectItem>
                <SelectItem value="quarter" data-id="element-927">Trimestre in corso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto pb-24" data-id="element-928">
        <div className="max-w-5xl mx-auto p-6 md:p-8 space-y-10" data-id="element-929">
          {/* Templates Hero */}
          <section data-id="element-930">
            <div className="mb-6" data-id="element-931">
              <h2 className="text-lg font-semibold flex items-center gap-2" data-id="element-932">
                <LayoutTemplate className="h-5 w-5 text-accent" data-id="element-933" />
                Scegli un template
              </h2>
              <p className="text-sm text-muted-foreground mt-1" data-id="element-934">
                Seleziona un formato di partenza. Potrai personalizzare le
                sezioni in seguito.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-935">
              {TEMPLATES.map(t => {
              const isSelected = template === t.id;
              return <motion.div key={t.id} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleTemplateChange(t.id)} className={`relative cursor-pointer rounded-xl border p-5 transition-all duration-200 flex flex-col h-full bg-card ${isSelected ? 'border-accent shadow-md ring-1 ring-accent/20' : 'border-border hover:border-accent/50 hover:shadow-sm'}`} data-id="element-936">
                    {isSelected && <div className="absolute -top-3 -right-3 w-7 h-7 bg-accent text-white rounded-full flex items-center justify-center shadow-sm z-10" data-id="element-937">
                        <Check className="h-4 w-4" data-id="element-938" />
                      </div>}

                    <div className="h-32 mb-5 rounded-lg overflow-hidden flex-shrink-0" data-id="element-939">
                      {t.preview}
                    </div>

                    <div className="mt-auto" data-id="element-940">
                      <div className="flex items-center justify-between mb-2" data-id="element-941">
                        <h3 className="font-semibold text-base" data-id="element-942">{t.name}</h3>
                        <span className="text-xs font-medium bg-muted px-2 py-1 rounded-md text-muted-foreground" data-id="element-943">
                          {t.sectionsCount} sezioni
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-944">
                        {t.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </section>

          <Separator data-id="element-945" />

          {/* Customize Sections */}
          <section data-id="element-946">
            <button onClick={() => setCustomizeOpen(!customizeOpen)} className="w-full flex items-center justify-between py-2 group" data-id="element-947">
              <div className="flex items-center gap-2" data-id="element-948">
                <Settings2 className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-949" />
                <h2 className="text-lg font-semibold group-hover:text-accent transition-colors" data-id="element-950">
                  Personalizza sezioni
                </h2>
              </div>
              {customizeOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" data-id="element-951" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" data-id="element-952" />}
            </button>

            <AnimatePresence data-id="element-953">
              {customizeOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-954">
                  <div className="pt-6 pb-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-955">
                    {AVAILABLE_SECTIONS.map(section => {
                  const isChecked = sections[section.id as keyof typeof sections];
                  return <div key={section.id} onClick={() => toggleSection(section.id as keyof typeof sections)} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${isChecked ? 'border-accent/50 bg-accent/5' : 'border-border bg-card hover:border-accent/30'}`} data-id="element-956">
                          <Checkbox checked={isChecked} onCheckedChange={() => toggleSection(section.id as keyof typeof sections)} className="mt-0.5" data-id="element-957" />
                          <span className="text-sm font-medium leading-snug select-none" data-id="element-958">
                            {section.label}
                          </span>
                        </div>;
                })}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </section>

          <Separator data-id="element-959" />

          {/* Notes Section */}
          <section data-id="element-960">
            <button onClick={() => setNotesOpen(!notesOpen)} className="w-full flex items-center justify-between py-2 group" data-id="element-961">
              <div className="flex items-center gap-2" data-id="element-962">
                <AlignLeft className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-963" />
                <h2 className="text-lg font-semibold group-hover:text-accent transition-colors" data-id="element-964">
                  Note aggiuntive
                </h2>
              </div>
              {notesOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" data-id="element-965" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" data-id="element-966" />}
            </button>

            <AnimatePresence data-id="element-967">
              {notesOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-968">
                  <div className="pt-6 pb-2" data-id="element-969">
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Inserisci un commento o una nota introduttiva per il cliente che apparirà all'inizio del report..." className="w-full px-4 py-3 text-sm border border-input rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-accent min-h-[120px] resize-y shadow-sm" data-id="element-970" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </section>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 p-4 md:px-8 z-20 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]" data-id="element-971">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-972">
          <div className="text-sm font-medium text-muted-foreground" data-id="element-973">
            <span className="text-foreground font-semibold" data-id="element-974">
              {selectedCount}
            </span>{' '}
            sezioni selezionate
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-975">
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" data-id="element-976">
              <Save className="h-4 w-4 mr-2" data-id="element-977" />
              Salva Bozza
            </Button>
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" data-id="element-978">
              <Download className="h-4 w-4 mr-2" data-id="element-979" />
              Esporta PDF
            </Button>
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" onClick={() => setPreviewOpen(true)} data-id="element-980">
              <Eye className="h-4 w-4 mr-2" data-id="element-981" />
              Anteprima
            </Button>
            <Button className="rounded-full flex-1 sm:flex-none px-8" data-id="element-982">
              Genera Report
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Overlay */}
      <AnimatePresence data-id="element-983">
        {previewOpen && createPortal(<motion.div key="preview-overlay" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="fixed inset-0 bg-white flex flex-col" style={{
        zIndex: 99999
      }} data-id="element-984">
              {/* Top bar - matching AI Mode style */}
              <div className="h-16 border-b border-border flex items-center justify-between px-6 flex-shrink-0 bg-white" data-id="element-985">
                <button type="button" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(false);
          }} className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-colors bg-transparent border-none outline-none" style={{
            zIndex: 100000
          }} aria-label="Chiudi anteprima" data-id="element-986">
                  <X className="h-5 w-5 text-foreground pointer-events-none" data-id="element-987" />
                </button>

                <div className="flex items-center gap-2" data-id="element-988">
                  <Eye className="h-4 w-4 text-accent" data-id="element-989" />
                  <span className="font-medium text-sm text-foreground" data-id="element-990">
                    Anteprima Report
                  </span>
                </div>

                <div className="flex items-center gap-3" data-id="element-991">
                  <Button variant="outline" className="rounded-full h-9 px-4 text-sm border-accent text-accent hover:bg-accent/10" data-id="element-992">
                    <Download className="h-4 w-4 mr-2" data-id="element-993" />
                    Esporta PDF
                  </Button>
                  <Button className="rounded-full h-9 px-4 text-sm" data-id="element-994">
                    Genera Report
                  </Button>
                </div>
              </div>

              {/* Document Area */}
              <div className="flex-1 overflow-auto bg-[#F0F1F3]" data-id="element-995">
                <div className="py-10 px-6" data-id="element-996">
                  <div className="w-[794px] min-h-[1123px] bg-white mx-auto shadow-[0_2px_20px_rgba(0,0,0,0.08)] flex flex-col p-16 text-foreground rounded-sm" data-id="element-997">
                    {/* Document Header */}
                    <div className="flex justify-between items-start mb-14" data-id="element-998">
                      <div data-id="element-999">
                        <div className="w-12 h-1 bg-accent mb-6 rounded-full" data-id="element-1000" />
                        <h1 className="text-[28px] font-bold mb-3 text-[#1A1F2E] leading-tight" data-id="element-1001">
                          {reportName}
                        </h1>
                        <p className="text-sm text-muted-foreground" data-id="element-1002">
                          Generato il{' '}
                          {new Date().toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1" data-id="element-1003">
                          Esclapon & Co. — Media Intelligence
                        </p>
                      </div>
                      <img src={intrumClient.logo} alt="Logo" className="h-8 object-contain mt-2" style={{
                  filter: 'brightness(0)'
                }} data-id="element-1004" />
                    </div>

                    {/* Notes */}
                    {notes && <div className="mb-10 text-[15px] leading-relaxed text-muted-foreground italic border-l-4 border-accent pl-5 py-1" data-id="element-1005">
                        {notes}
                      </div>}

                    {/* Sections */}
                    <div className="space-y-12 flex-1" data-id="element-1006">
                      {sections.summary && <section data-id="element-1007">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1008">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1009" />
                            Executive Summary
                          </h2>
                          <div className="text-[15px] leading-relaxed text-muted-foreground space-y-3 pl-4" data-id="element-1010">
                            <p data-id="element-1011">
                              Nel periodo analizzato, Intrum ha registrato una
                              copertura mediatica significativa con 20 articoli
                              pubblicati su 12 testate nazionali e
                              specializzate. Il sentiment complessivo è
                              prevalentemente positivo (65%), con un AVE totale
                              di €187k.
                            </p>
                            <p data-id="element-1012">
                              I temi principali riguardano le operazioni di
                              gestione dei crediti deteriorati (NPL), le
                              strategie di espansione nel mercato italiano e il
                              posizionamento competitivo nel settore del credit
                              management.
                            </p>
                          </div>
                        </section>}

                      {sections.kpi && <section data-id="element-1013">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1014">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1015" />
                            KPI Principali
                          </h2>
                          <div className="grid grid-cols-2 gap-4 pl-4" data-id="element-1016">
                            <div className="p-5 border border-border rounded-lg" data-id="element-1017">
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1018">
                                Articoli totali
                              </div>
                              <div className="text-3xl font-bold text-[#1A1F2E]" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1019">
                                20
                              </div>
                              <div className="text-xs text-green-600 mt-1" data-id="element-1020">
                                +25% vs periodo prec.
                              </div>
                            </div>
                            <div className="p-5 border border-border rounded-lg" data-id="element-1021">
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1022">
                                Score medio
                              </div>
                              <div className="text-3xl font-bold text-accent" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1023">
                                7.4
                              </div>
                              <div className="text-xs text-green-600 mt-1" data-id="element-1024">
                                +8.8% vs periodo prec.
                              </div>
                            </div>
                            <div className="p-5 border border-border rounded-lg" data-id="element-1025">
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1026">
                                AVE totale
                              </div>
                              <div className="text-3xl font-bold text-[#1A1F2E]" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1027">
                                €187k
                              </div>
                              <div className="text-xs text-green-600 mt-1" data-id="element-1028">
                                +31.7% vs periodo prec.
                              </div>
                            </div>
                            <div className="p-5 border border-border rounded-lg" data-id="element-1029">
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1030">
                                Sentiment
                              </div>
                              <div className="flex items-baseline gap-2" data-id="element-1031">
                                <span className="text-3xl font-bold text-accent" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1032">
                                  65%
                                </span>
                                <span className="text-sm text-muted-foreground" data-id="element-1033">
                                  positivo
                                </span>
                              </div>
                              <div className="flex gap-1 mt-2" data-id="element-1034">
                                <div className="h-1.5 rounded-full bg-accent" style={{
                          width: '65%'
                        }} data-id="element-1035" />
                                <div className="h-1.5 rounded-full bg-[#64B5F6]" style={{
                          width: '25%'
                        }} data-id="element-1036" />
                                <div className="h-1.5 rounded-full bg-[#1A3A5C]" style={{
                          width: '10%'
                        }} data-id="element-1037" />
                              </div>
                            </div>
                          </div>
                        </section>}

                      {sections.topArticles && <section data-id="element-1038">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1039">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1040" />
                            Top Articoli
                          </h2>
                          <div className="space-y-0 pl-4" data-id="element-1041">
                            {[{
                      title: 'Intrum acquisisce portafoglio NPL da €1.2 miliardi da UniCredit',
                      source: 'Il Sole 24 Ore',
                      score: 9.2,
                      date: '15 Apr'
                    }, {
                      title: 'Credit management: Intrum rafforza la presenza in Italia',
                      source: 'MF Milano Finanza',
                      score: 8.7,
                      date: '12 Apr'
                    }, {
                      title: 'Il mercato NPL italiano attira gli investitori nordeuropei',
                      source: 'Corriere della Sera',
                      score: 8.1,
                      date: '10 Apr'
                    }].map((article, i) => <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0" data-id="element-1042">
                                <div className="flex-1 min-w-0 pr-4" data-id="element-1043">
                                  <div className="text-[14px] font-medium text-[#1A1F2E] leading-snug" data-id="element-1044">
                                    {article.title}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1" data-id="element-1045">
                                    {article.source} · {article.date}
                                  </div>
                                </div>
                                <div className="text-sm font-semibold text-accent flex-shrink-0" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1046">
                                  {article.score}
                                </div>
                              </div>)}
                          </div>
                        </section>}

                      {sections.sentiment && <section data-id="element-1047">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1048">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1049" />
                            Andamento Sentiment
                          </h2>
                          <div className="h-48 bg-[#F8F9FA] border border-border rounded-lg flex items-center justify-center ml-4" data-id="element-1050">
                            <div className="flex flex-col items-center gap-2 text-muted-foreground" data-id="element-1051">
                              <BarChart3 className="h-8 w-8 text-accent/40" data-id="element-1052" />
                              <span className="text-sm" data-id="element-1053">
                                Grafico Sentiment nel tempo
                              </span>
                            </div>
                          </div>
                        </section>}

                      {sections.tier && <section data-id="element-1054">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1055">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1056" />
                            Copertura per Tier
                          </h2>
                          <div className="space-y-3 pl-4" data-id="element-1057">
                            {[{
                      tier: 'Tier 1',
                      count: 5,
                      pct: 25
                    }, {
                      tier: 'Tier 2',
                      count: 8,
                      pct: 40
                    }, {
                      tier: 'Tier 3',
                      count: 4,
                      pct: 20
                    }, {
                      tier: 'Specializzate',
                      count: 3,
                      pct: 15
                    }].map(item => <div key={item.tier} data-id="element-1058">
                                <div className="flex items-center justify-between mb-1.5 text-sm" data-id="element-1059">
                                  <span className="font-medium text-[#1A1F2E]" data-id="element-1060">
                                    {item.tier}
                                  </span>
                                  <span className="text-muted-foreground" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1061">
                                    {item.count} articoli ({item.pct}%)
                                  </span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1062">
                                  <div className="h-full bg-accent rounded-full" style={{
                          width: `${item.pct}%`
                        }} data-id="element-1063" />
                                </div>
                              </div>)}
                          </div>
                        </section>}

                      {sections.sources && <section data-id="element-1064">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1065">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1066" />
                            Top Testate
                          </h2>
                          <div className="space-y-0 pl-4" data-id="element-1067">
                            {[{
                      name: 'Il Sole 24 Ore',
                      articles: 4,
                      score: 8.5
                    }, {
                      name: 'MF Milano Finanza',
                      articles: 3,
                      score: 8.1
                    }, {
                      name: 'Corriere della Sera',
                      articles: 2,
                      score: 7.8
                    }, {
                      name: 'La Repubblica',
                      articles: 2,
                      score: 7.2
                    }, {
                      name: 'Il Messaggero',
                      articles: 2,
                      score: 6.9
                    }].map(source => <div key={source.name} className="flex items-center justify-between py-2.5 border-b border-border last:border-0 text-sm" data-id="element-1068">
                                <span className="font-medium text-[#1A1F2E]" data-id="element-1069">
                                  {source.name}
                                </span>
                                <div className="flex items-center gap-6 text-muted-foreground" data-id="element-1070">
                                  <span style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1071">
                                    {source.articles} art.
                                  </span>
                                  <span className="font-semibold text-[#1A1F2E] w-8 text-right" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1072">
                                    {source.score}
                                  </span>
                                </div>
                              </div>)}
                          </div>
                        </section>}

                      {sections.spokespersons && <section data-id="element-1073">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1074">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1075" />
                            Portavoce citati
                          </h2>
                          <div className="grid grid-cols-2 gap-3 pl-4" data-id="element-1076">
                            {[{
                      name: 'Andrea Mangoni',
                      role: 'CEO Italia',
                      mentions: 8
                    }, {
                      name: 'Lars Eriksson',
                      role: 'Group CFO',
                      mentions: 4
                    }, {
                      name: 'Marco Bianchi',
                      role: 'Head of NPL',
                      mentions: 3
                    }, {
                      name: 'Sofia Rossi',
                      role: 'Head of Communications',
                      mentions: 2
                    }].map(person => <div key={person.name} className="p-3 border border-border rounded-lg flex items-center justify-between" data-id="element-1077">
                                <div data-id="element-1078">
                                  <div className="text-sm font-medium text-[#1A1F2E]" data-id="element-1079">
                                    {person.name}
                                  </div>
                                  <div className="text-xs text-muted-foreground" data-id="element-1080">
                                    {person.role}
                                  </div>
                                </div>
                                <div className="text-lg font-semibold text-accent" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1081">
                                  {person.mentions}
                                </div>
                              </div>)}
                          </div>
                        </section>}

                      {sections.allArticles && <section data-id="element-1082">
                          <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1083">
                            <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1084" />
                            Lista completa articoli
                          </h2>
                          <div className="border border-border rounded-lg overflow-hidden ml-4" data-id="element-1085">
                            <div className="bg-[#F8F9FA] px-4 py-2.5 flex gap-4 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1086">
                              <div className="w-20" data-id="element-1087">Data</div>
                              <div className="w-32" data-id="element-1088">Testata</div>
                              <div className="flex-1" data-id="element-1089">Titolo</div>
                              <div className="w-14 text-right" data-id="element-1090">Score</div>
                            </div>
                            {[{
                      date: '15/04',
                      source: 'Il Sole 24 Ore',
                      title: 'Intrum acquisisce portafoglio NPL da €1.2 miliardi',
                      score: '9.2'
                    }, {
                      date: '14/04',
                      source: 'MF Milano Finanza',
                      title: 'Credit management: Intrum rafforza la presenza',
                      score: '8.7'
                    }, {
                      date: '12/04',
                      source: 'Corriere della Sera',
                      title: 'Il mercato NPL italiano attira investitori',
                      score: '8.1'
                    }, {
                      date: '10/04',
                      source: 'La Repubblica',
                      title: 'Crediti deteriorati, il ruolo dei servicer nordeuropei',
                      score: '7.6'
                    }, {
                      date: '08/04',
                      source: 'Il Messaggero',
                      title: 'Intrum: piano di espansione nel Lazio',
                      score: '7.2'
                    }].map((row, i) => <div key={i} className="px-4 py-2.5 flex gap-4 border-b border-border last:border-0 text-sm" data-id="element-1091">
                                <div className="w-20 text-muted-foreground" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1092">
                                  {row.date}
                                </div>
                                <div className="w-32 text-muted-foreground truncate" data-id="element-1093">
                                  {row.source}
                                </div>
                                <div className="flex-1 text-[#1A1F2E] font-medium truncate" data-id="element-1094">
                                  {row.title}
                                </div>
                                <div className="w-14 text-right font-semibold" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1095">
                                  {row.score}
                                </div>
                              </div>)}
                          </div>
                        </section>}
                    </div>

                    {/* Footer */}
                    <div className="mt-16 pt-5 border-t border-border flex items-center justify-between" data-id="element-1096">
                      <div className="text-xs text-muted-foreground" data-id="element-1097">
                        Esclapon & Co. — Media Intelligence
                      </div>
                      <div className="text-xs text-muted-foreground" data-id="element-1098">
                        Report generato automaticamente ·{' '}
                        {new Date().toLocaleDateString('it-IT')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>, document.body)}
      </AnimatePresence>
    </div>;
}